import { makeStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import DeleteIcon from "@material-ui/icons/Delete";
import { TypedBannerBulkDelete } from "@saleor/banners/mutations";
import { BannerBulkDelete } from "@saleor/banners/types/BannerBulkDelete";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { WindowTitle } from "@saleor/components/WindowTitle";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { BannerVerticalEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import BannerListPage from "../../components/BannerListPage";
import { useBannerListQuery } from "../../queries";
import {
  bannerAddUrl,
  bannerListUrl,
  BannerListUrlDialog,
  BannerListUrlQueryParams,
  bannerUrl
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterVariables,
  saveFilterTab
} from "./filters";

const useStyles = makeStyles(
  {
    deleteIcon: {
      marginRight: "8px"
    }
  },
  { name: "BannerList" }
);

interface BannerListProps {
  params: BannerListUrlQueryParams;
}

export const BannerList: React.FC<BannerListProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const classes = useStyles("");

  const shop = useShop();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.BANNER_LIST
  );
  const intl = useIntl();

  const paginationState = createPaginationState(settings.rowNumber, params);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch: refetchBannerList } = useBannerListQuery({
    displayLoader: true,
    variables: queryVariables
  });

  const verticalTabs = [
    ...[
      {
        data: {
          verticals: []
        },
        name: "All"
      },
      {
        data: {
          verticals: BannerVerticalEnum.HOMEPAGE
        },
        name: "Homepage"
      },
      {
        data: {
          verticals: BannerVerticalEnum.FOOD
        },
        name: "Food"
      },
      {
        data: {
          verticals: BannerVerticalEnum.GROCERY
        },
        name: "Grocery"
      },
      {
        data: {
          verticals: BannerVerticalEnum.SHOPS
        },
        name: "Shops"
      },
      {
        data: {
          verticals: BannerVerticalEnum.SM_FULFILLED
        },
        name: "ShopSM"
      },
      {
        data: {
          verticals: BannerVerticalEnum.CINEMA
        },
        name: "Cinema"
      },
      {
        data: {
          verticals: BannerVerticalEnum.IN_MALL_DEALS
        },
        name: "In Mall Deals"
      }
    ]
  ];

  React.useEffect(() => {
    refetchBannerList();
  }, [settings.rowNumber]);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: bannerListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? verticalTabs.length
        : 0
      : parseInt(params.activeTab, 0);

  const [openModal, closeModal] = createDialogActionHandlers<
    BannerListUrlDialog,
    BannerListUrlQueryParams
  >(navigate, bannerListUrl, params);

  const handleTabChange = (tab: string | number) => {
    reset();
    navigate(
      bannerListUrl({
        ...params,
        activeTab: tab.toString(),
        ...verticalTabs[tab].data
      })
    );
  };

  const handleSearch = (text: string) => {
    navigate(bannerListUrl({ ...params, query: text }));
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    reset();
    navigate(bannerListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(verticalTabs.length + 1);
  };

  const canOpenBulkActionDialog = maybe(() => params.ids.length > 0);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.banners.pageInfo),
    paginationState,
    params
  );

  const handleBannerBulkDeactivate = (data: BannerBulkDelete) => {
    if (data.bannerBulkDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Banners successfully deleted."
        })
      });
      reset();
      closeModal();
      refetchBannerList();
    } else if (data.bannerBulkDelete.errors.length !== 0) {
      closeModal();
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Unable to delete. banner promo is ongoing."
        })
      });
      reset();
      refetchBannerList();
    }
  };

  const handleSort = createSortHandler(navigate, bannerListUrl, params);
  const currencySymbol = maybe(() => shop.defaultCurrency, "PHP");

  return (
    <TypedBannerBulkDelete onCompleted={handleBannerBulkDeactivate}>
      {(bannerBulkDelete, bannerBulkDeleteOpts) => {
        const onBannerBulkDelete = () =>
          bannerBulkDelete({
            variables: {
              ids: params.ids
            }
          });

        return (
          <>
            <WindowTitle title={intl.formatMessage(sectionNames.banners)} />
            <BannerListPage
              brands={null}
              activeTab={params.activeTab}
              currencySymbol={currencySymbol}
              currentTab={currentTab}
              filterOpts={getFilterOpts(params)}
              initialSearch={params.query || ""}
              onSearchChange={handleSearch}
              onFilterChange={filter => changeFilters(filter, true)}
              onAll={resetFilters}
              onTabChange={handleTabChange}
              onTabDelete={() => openModal("delete-search")}
              onTabSave={() => openModal("save-search")}
              tabs={verticalTabs.map(tab => tab.name)}
              defaultCurrency={maybe(() => shop.defaultCurrency)}
              banners={maybe(() => data.banners.edges.map(edge => edge.node))}
              settings={settings}
              disabled={loading}
              pageInfo={pageInfo}
              onAdd={() => navigate(bannerAddUrl)}
              onNextPage={loadNextPage}
              onPreviousPage={loadPreviousPage}
              onSort={handleSort}
              onUpdateListSettings={updateListSettings}
              onRowClick={id => () => navigate(bannerUrl(id))}
              isChecked={isSelected}
              selected={listElements.length}
              sort={getSortParams(params)}
              toggle={toggle}
              toggleAll={toggleAll}
              totalCount={maybe(() => data.banners.totalCount)}
              toolbar={
                <Button
                  color="primary"
                  onClick={() =>
                    openModal("delete", {
                      ids: listElements
                    })
                  }
                >
                  <DeleteIcon className={classes.deleteIcon} />
                </Button>
              }
              customAction={undefined}
            />
            <ActionDialog
              confirmButtonState={bannerBulkDeleteOpts.status}
              onClose={closeModal}
              onConfirm={onBannerBulkDelete}
              open={params.action === "delete" && canOpenBulkActionDialog}
              title={intl.formatMessage({
                defaultMessage: "Delete banners",
                description: "dialog header"
              })}
              confirmButtonLabel="Delete"
              variant="delete"
            >
              {canOpenBulkActionDialog && (
                <DialogContentText>
                  <FormattedMessage
                    defaultMessage="{counter,plural,one{Are you sure you want to delete this banner?} other{Are you sure you want to delete {displayQuantity} banners?}}"
                    description="dialog content"
                    values={{
                      counter: params.ids.length,
                      displayQuantity: <strong>{params.ids.length}</strong>
                    }}
                  />
                </DialogContentText>
              )}
            </ActionDialog>
            <SaveFilterTabDialog
              open={params.action === "save-search"}
              confirmButtonState="default"
              onClose={closeModal}
              onSubmit={handleTabSave}
            />
            <DeleteFilterTabDialog
              open={params.action === "delete-search"}
              confirmButtonState="default"
              onClose={closeModal}
              onSubmit={handleTabDelete}
              tabName={maybe(() => verticalTabs[currentTab - 1].name, "...")}
            />
          </>
        );
      }}
    </TypedBannerBulkDelete>
  );
};

export default BannerList;
